import { DocumentTextIcon } from '@heroicons/react/24/outline';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Routes, useNavigate, useParams } from 'react-router-dom';

import { getContracts } from '../../api/contracts';
import { getModuleCategories } from '../../api/modules';

import ProtectedRoute from '../../components/helpers/ProtectedRoute';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import {
	List,
	Badge,
	FilterDropdown,
	TextStack,
	ProgressBar,
	DataTable,
} from '../../components/nextgen';

import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';
import useSearchParams from '../../hooks/useSearchParams';
import usePrevious from '../../hooks/usePrevious';

import ContractForm from './ContractForm';

const CONTRACT_MODULE_ID = process.env.REACT_APP_MODULE_ID_CONTRACT;

export const Contracts = () => {
	const navigate = useNavigate();
	const { id } = useParams();

	// Separate state for status and category filters
	const [statusFilter, setStatusFilter] = useState('active');
	const [categoryFilter, setCategoryFilter] = useState('all');

	const { open: contractOverviewOpen, setOpen: openContractModal } =
		useModal('contractOverview');

	const { open: isAddNewContractModalOpen, setOpen: openAddNewContractModal } =
		useModal('contractAdd');

	const {
		page: pageFilter,
		status: statusParam,
		category: categoryParam,
		sortBy: sortByFilter,
		sortOrder: sortOrderFilter,
	} = useSearchParams();

	const { value: contracts, execute: refreshContracts } = useAsync(
		getContracts,
		{
			defaultPayload: { categoryId: 'active', pageSize: null },
		},
	);
	const { value: categories } = useAsync(getModuleCategories, {
		defaultPayload: { id: CONTRACT_MODULE_ID },
		silent: true,
	});

	// Sync URL params to state on mount/change
	useEffect(() => {
		if (statusParam) setStatusFilter(statusParam);
		if (categoryParam) setCategoryFilter(categoryParam);
	}, [statusParam, categoryParam]);

	// Fetch contracts when filters change
	useEffect(() => {
		const params = {
			page: pageFilter,
			sortBy: sortByFilter,
			sortOrder: sortOrderFilter,
		};

		// If a specific category is selected, use it; otherwise use the status filter
		if (categoryFilter && categoryFilter !== 'all') {
			params.categoryId = categoryFilter;
		} else {
			params.categoryId = statusFilter || 'active';
		}

		refreshContracts(params, null, { silent: true });
	}, [pageFilter, statusFilter, categoryFilter, sortByFilter, sortOrderFilter]);

	const prevContractOverviewOpen = usePrevious(contractOverviewOpen);
	useEffect(() => {
		if (!contractOverviewOpen && prevContractOverviewOpen) {
			// Refresh with current filters
			const params = {
				categoryId: categoryFilter !== 'all' ? categoryFilter : statusFilter,
			};
			refreshContracts(params);
		}
	}, [contractOverviewOpen]);

	useEffect(() => {
		if (id) {
			openContractModal({
				id,
				path: `/manage/contracts`,
			});
		}
	}, [id]);

	const prevNewContractModal = usePrevious(isAddNewContractModalOpen);
	useEffect(() => {
		if (!isAddNewContractModalOpen && prevNewContractModal) {
			const params = {
				page: pageFilter,
				categoryId: categoryFilter !== 'all' ? categoryFilter : statusFilter,
				sortBy: sortByFilter,
				sortOrder: sortOrderFilter,
			};
			refreshContracts(params, null, { silent: true });
		}
	}, [isAddNewContractModalOpen]);

	// Update URL when filters change
	const updateFilters = (newStatus, newCategory) => {
		const search = new URLSearchParams();

		if (newStatus && newStatus !== 'active') {
			search.append('status', newStatus);
		}
		if (newCategory && newCategory !== 'all') {
			search.append('category', newCategory);
		}

		navigate({ search: search.toString() });
		setStatusFilter(newStatus);
		setCategoryFilter(newCategory);
	};

	const handleStatusChange = (value) => {
		updateFilters(value, categoryFilter);
	};

	const handleCategoryChange = (value) => {
		updateFilters(statusFilter, value);
	};

	// Build category options for the filter dropdown
	const categoryOptions = [
		{ value: 'all', label: 'All Categories' },
		...(categories?.map((cat) => ({ value: cat.id, label: cat.name })) || []),
		{ value: 'uncategorized', label: 'Uncategorized' },
	];

	// Status options for filtering
	const statusOptions = [
		{ value: 'all', label: 'All Contracts' },
		{ value: 'active', label: 'Active' },
		{ value: 'expiring', label: 'Expiring Soon' },
		{ value: 'expired', label: 'Expired' },
		{ value: 'voided', label: 'Voided' },
	];

	return (
		<MainContent maxWidth="max-w-7xl">
			<PageHeading
				actions={[
					{
						name: 'Add New Contract',
						onClick: () => openAddNewContractModal(),
					},
				]}
				divider={false}
			>
				Manage Contracts
			</PageHeading>

			{/* Filters */}
			<div className="flex items-center gap-3 mb-6">
				<FilterDropdown
					label="Status"
					placeholder="All Contracts"
					value={statusFilter}
					options={statusOptions}
					onChange={handleStatusChange}
				/>
				<FilterDropdown
					label="Category"
					placeholder="All Categories"
					value={categoryFilter}
					options={categoryOptions}
					onChange={handleCategoryChange}
				/>
			</div>

			{/* List */}
			<List
				data={contracts?.rows || []}
				keyField="id"
				renderItem={(contract) => {
					const name = contract?.name || 'N/A';
					const contractEndDate = contract?.endDate;
					const contractStartDate = contract?.startDate;

					const isExpired =
						moment().isAfter(moment(contractEndDate).endOf('d')) ||
						contract?.active === false;

					const isExpiringSoon = moment().isAfter(
						moment(contractEndDate).subtract(30, 'days').endOf('d'),
					);

					const isUpcoming = moment().isBefore(moment(contractStartDate));

					const variant = isExpired
						? 'error'
						: isExpiringSoon
							? 'warning'
							: isUpcoming
								? 'default'
								: 'success';

					const progressColor = isExpired
						? 'error'
						: isExpiringSoon
							? 'warning'
							: isUpcoming
								? 'default'
								: 'success';

					const label =
						contract?.active === false
							? 'Voided'
							: isExpired
								? `Expired ${moment(contractEndDate).fromNow()}`
								: isExpiringSoon
									? `Expiring ${moment(contractEndDate).fromNow()}`
									: isUpcoming
										? `Upcoming ${moment(contractStartDate).fromNow()}`
										: 'Active';

					const days = moment(contractEndDate).diff(moment(), 'days');
					const totalDays = moment(contractEndDate).diff(
						moment(contractStartDate),
						'days',
					);

					return (
						<>
							<div className="flex justify-between items-center">
								<TextStack
									primary={name}
									secondary={contract?.category?.name || 'Uncategorized'}
									className="w-[300px] truncate pr-2"
								/>
								<div className="flex-1">
									<ProgressBar
										label={
											<>
												<Badge
													variant={variant}
													style={'outline'}
													className="mr-3"
												>
													{label}
												</Badge>
												{`${moment(contractStartDate).format('MMM D, YYYY')} - ${moment(contractEndDate).format('MMM D, YYYY')}`}
											</>
										}
										current={days >= 0 ? days : 0}
										max={totalDays}
										valueLabel={`${days >= 0 ? days : 0} days remaining`}
										color={progressColor}
										inverse
									/>
								</div>
							</div>
						</>
					);
				}}
				onItemClick={(contract) => {
					navigate(`/manage/contracts/${contract?.id}`);
				}}
				emptyTitle="No contracts found"
				emptyDescription="Create a new contract to get started"
			/>
		</MainContent>
	);
};

export default {};

/*export default () => (
	<Routes>
		<ProtectedRoute
			exact
			path={`/manage/contracts/new`}
			component={ContractForm}
		/>
		<ProtectedRoute
			exact
			path={`/manage/contracts/:id/edit`}
			component={ContractForm}
		/>
		<ProtectedRoute exact path={`/manage/contracts`} component={Contracts} />
		<ProtectedRoute
			exact
			path={`/manage/contracts/:id`}
			component={Contracts}
		/>
	</Routes>
);*/
